export const toSaleNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
};

export const getSaleDateKey = (value) => String(value || "").slice(0, 10);

export const normalizeSaleStatus = (value) => {
  const raw = String(value || "").trim();
  const key = raw.toUpperCase().replace(/[\s-]+/g, "_");

  if (["DELIVERED", "DELIVER", "DELIVERY"].includes(key)) return "Delivered";
  if (["CANCEL", "CANCELLED", "CANCELED"].includes(key)) return "Cancel";
  if (["RETURN", "RTO", "DTO"].includes(key)) return "Return";
  return raw || "-";
};

const getRowQuantity = (row) => {
  const quantity = toSaleNumber(row?.total_qty ?? row?.quantity ?? row?.qty ?? row?.total_unit);
  return quantity > 0 ? quantity : 1;
};

const getProductEntries = (row) => {
  const raw = String(row?.items || row?.items_summary || row?.productName || row?.product || "");
  const entries = raw.split(",").map((entry) => entry.trim()).filter(Boolean);

  if (!entries.length) return [{ product: "-", unit: getRowQuantity(row) }];

  return entries.map((entry) => {
    const match = entry.match(/^(.*?)\s*\((\d+(?:\.\d+)?)\)\s*$/);
    return {
      product: (match ? match[1] : entry).trim() || "-",
      unit: match ? toSaleNumber(match[2]) : 0,
    };
  });
};

export const getSaleAmount = (row) => {
  if (row?.amount !== undefined && row?.amount !== null) return toSaleNumber(row.amount);
  return toSaleNumber(row?.total_amount || row?.sale_price || row?.settlement_amount) +
    toSaleNumber(row?.packaging_cost);
};

// The API may return repeated rows for one sale/AWB. Count the API's total_qty
// once per AWB and keep product breakdown quantities separately for Product Sale.
export const buildDeliveredSaleRows = (sales) => {
  const orders = new Map();

  for (const row of sales || []) {
    const status = normalizeSaleStatus(row?.status || row?.tracking_status);
    if (["Cancel", "Return"].includes(status)) continue;

    const key = `${row?.id ?? "row"}|${row?.sales_awb_id ?? row?.awb_no ?? ""}`;
    let order = orders.get(key);
    if (!order) {
      order = {
        id: `sale-${key}`,
        date: row.sale_date || row.created_at,
        platform: row.platform || "DIRECT",
        account: row.account_name || row.customer_name || "-",
        status: "Delivered",
        amount: 0,
        unit: 0,
        products: new Map(),
      };
      orders.set(key, order);
    }

    order.amount += getSaleAmount(row);
    // sales_awb rows can repeat when a sale has several items. Prefer the
    // distinct product quantities, while using API total_qty for malformed
    // or missing item descriptions.
    const describedQuantity = getProductEntries(row).reduce((sum, item) => sum + item.unit, 0);
    order.unit = Math.max(order.unit, describedQuantity || getRowQuantity(row));
    for (const item of getProductEntries(row)) {
      const productKey = item.product.toLowerCase();
      const product = order.products.get(productKey);
      if (product) {
        product.unit += item.unit;
      } else {
        order.products.set(productKey, { product: item.product, unit: item.unit });
      }
    }
  }

  return Array.from(orders.values()).flatMap((order) => {
    const products = Array.from(order.products.values());
    return products.map((item) => ({
      ...order,
      id: `${order.id}-${item.product.toLowerCase()}`,
      product: `${item.product} (${item.unit})`,
      unit: order.unit,
      productUnit: item.unit,
      amount: order.amount,
    }));
  });
};

export const groupProductSales = (rows, { fromDate = "", toDate = "", search = "" } = {}) => {
  const grouped = new Map();
  const term = search.trim().toLowerCase();

  for (const row of rows || []) {
    const date = getSaleDateKey(row.date);
    if (!date || (fromDate && date < fromDate) || (toDate && date > toDate)) continue;

    const match = String(row.product || "").match(/^(.*?)\s*\((\d+(?:\.\d+)?)\)\s*$/);
    const product = (match ? match[1] : row.product || "-").trim() || "-";
    if (term && !product.toLowerCase().includes(term)) continue;

    const status = normalizeSaleStatus(row.status);
    const key = `${date}|${product.toLowerCase()}|${status}`;
    const existing = grouped.get(key);
    if (existing) {
      existing.unit = Math.max(existing.unit, toSaleNumber(row.unit));
      existing.amount += toSaleNumber(row.amount);
    } else {
      grouped.set(key, {
        id: key,
        date: row.date,
        product,
        unit: toSaleNumber(row.unit),
        amount: toSaleNumber(row.amount),
        status,
      });
    }
  }

  return Array.from(grouped.values());
};

// This mirrors Total Sales grouping: a product/day/status group counts its
// represented order-unit once (max), while amounts still add across orders.
export const getTotalSalesUnit = (rows) =>
  (rows || []).reduce((sum, row) => sum + toSaleNumber(row.unit), 0);

// Match Total Sales exactly: aggregate orders by date, product label and
// status, keep the largest represented order quantity for each group.
export const getTotalSalesSummaryUnit = (rows, { fromDate = "", toDate = "" } = {}) => {
  const grouped = new Map();

  for (const row of rows || []) {
    const date = getSaleDateKey(row.date);
    if (!date || (fromDate && date < fromDate) || (toDate && date > toDate)) continue;

    const key = `${date}|${String(row.product || "-").trim().toLowerCase()}|${normalizeSaleStatus(row.status)}`;
    grouped.set(key, Math.max(grouped.get(key) || 0, toSaleNumber(row.unit)));
  }

  return getTotalSalesUnit(Array.from(grouped.values()).map((unit) => ({ unit })));
};
